
import { useNavigate } from 'react-router-dom';
import Header from '../../home/components/Header';
import Footer from '../../home/components/Footer';
import SeoHead from '../../../components/feature/SeoHead';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://www.cherokeeyouthwarriors.com';

const footballSchema = {
  '@context': 'https://schema.org',
  '@type': 'SportsEvent',
  name: 'Cherokee Youth Football Program 2026 Season',
  description: 'K-5 youth tackle football program in Canton, GA. Teams for grades K1 through 5th grade with experienced coaches, equipment provided, and a full game schedule.',
  url: `${siteUrl}/programs/football`,
  location: {
    '@type': 'Place',
    name: 'Teasley Middle / New Cherokee High School',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Canton',
      addressRegion: 'GA',
      addressCountry: 'US',
    },
  },
  organizer: {
    '@type': 'SportsOrganization',
    name: 'Cherokee Youth Football & Cheer',
    url: siteUrl,
  },
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
      { '@type': 'ListItem', position: 2, name: 'Football Program', item: `${siteUrl}/programs/football` },
    ],
  },
};

export default function FootballPage() {
  const navigate = useNavigate();
  const REGISTRATION_URL = 'https://cobbfootball.leagueapps.com/clubteams/4926021-2026-cherokee-youth-football--cheerleading';

  const gradeLevels = [
    { grade: 'K1', subtitle: '8-man tackle football', coach: 'Kai Rak' },
    { grade: '2nd Grade', subtitle: '', coach: 'Justin Osborne' },
    { grade: '3rd Grade', subtitle: '', coach: 'Evan Ward' },
    { grade: '4th Grade', subtitle: '', coach: 'JD Hurd' },
    { grade: '5th Grade', subtitle: '', coach: 'Josh Bagby', note: 'If split, Steve Canty will also Head Coach.' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="Youth Football Program Canton GA | Cherokee Warriors K-5"
        description="Cherokee Youth Football program in Canton, GA offers K-5 tackle football teams with experienced coaches, equipment provided, and a full season schedule. Register for 2026."
        keywords="youth football Canton GA, Cherokee Warriors football, K-5 football program, youth tackle football Georgia"
        canonical={`${siteUrl}/programs/football`}
        schemaJson={footballSchema}
      />
      <Header />

      <section className="relative pt-32 pb-20 bg-gradient-to-br from-warrior-black via-gray-900 to-warrior-black overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src="https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/16dde075b9bffe943c9ba559d8eb943d.jpeg" alt="Football Hero Background" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <button onClick={() => navigate('/')} className="inline-flex items-center space-x-2 text-white/80 hover:text-white font-sans text-sm mb-6 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line text-lg"></i>
            <span>Back to Home</span>
          </button>
          <h1 className="font-display text-6xl sm:text-7xl lg:text-8xl text-white mb-6 leading-none">
            FOOTBALL<br />PROGRAM
          </h1>
          <p className="font-sans text-lg text-white/90 max-w-2xl mx-auto">
            Building champions on and off the field through discipline, teamwork, and dedication
          </p>
        </div>
      </section>

      <section className="py-20 bg-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-5xl text-warrior-black mb-4">GRADE LEVELS</h2>
            <p className="font-sans text-gray-600 text-sm max-w-2xl mx-auto">
              We offer competitive football teams for grades K1 through 5th grade. Each team is led by experienced coaches dedicated to player development.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {gradeLevels.map((level) => (
              <div key={level.grade} className="bg-white rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                <div className="w-16 h-16 flex items-center justify-center bg-warrior-red rounded-full mb-4 overflow-hidden">
                  <img src="https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/80219af022d52b64a8832b24f76b499d.png" alt="Football" className="w-10 h-10 object-contain" />
                </div>
                <h3 className="font-display text-3xl text-warrior-black mb-1">{level.grade}</h3>
                {level.subtitle && <p className="font-sans text-xs text-warrior-red font-semibold mb-3">{level.subtitle}</p>}
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-user-line text-base text-warrior-red"></i>
                    </div>
                    <div>
                      <p className="font-sans text-xs text-gray-500 uppercase tracking-wide">Head Coach</p>
                      <p className="font-sans text-sm text-gray-700 font-semibold">{level.coach}</p>
                    </div>
                  </div>
                  {level.note && (
                    <div className="flex items-start space-x-2 pt-1">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-information-line text-base text-warrior-red"></i>
                      </div>
                      <p className="font-sans text-xs text-gray-500 italic">{level.note}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-5xl text-warrior-black mb-4">WHAT'S INCLUDED</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-gray-100 rounded-2xl p-8">
              <div className="w-16 h-16 flex items-center justify-center bg-warrior-black rounded-full mb-6">
                <i className="ri-shield-line text-3xl text-white"></i>
              </div>
              <h3 className="font-display text-2xl text-warrior-black mb-4">EQUIPMENT PROVIDED</h3>
              <ul className="space-y-2 font-sans text-sm text-gray-700">
                {['Jersey', 'Helmet', 'Shoulder pads'].map(item => (
                  <li key={item} className="flex items-start space-x-2">
                    <i className="ri-check-line text-warrior-red mt-0.5"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-gray-100 rounded-2xl p-8">
              <div className="w-16 h-16 flex items-center justify-center bg-warrior-black rounded-full mb-6">
                <i className="ri-calendar-line text-3xl text-white"></i>
              </div>
              <h3 className="font-display text-2xl text-warrior-black mb-4">PRACTICE SCHEDULE</h3>
              <ul className="space-y-2 font-sans text-sm text-gray-700">
                <li className="flex items-start space-x-2"><i className="ri-time-line text-warrior-red mt-0.5"></i><span><strong>Weekdays:</strong> 3 practices per week (Mon, Tues, Thurs.)</span></li>
                <li className="flex items-start space-x-2"><i className="ri-time-line text-warrior-red mt-0.5"></i><span><strong>Duration:</strong> 6-8pm</span></li>
                <li className="flex items-start space-x-2"><i className="ri-time-line text-warrior-red mt-0.5"></i><span><strong>Location:</strong> Teasley Middle or New Cherokee High</span></li>
                <li className="flex items-start space-x-2"><i className="ri-time-line text-warrior-red mt-0.5"></i><span><strong>Season:</strong> July through November</span></li>
              </ul>
            </div>
            <div className="bg-gray-100 rounded-2xl p-8">
              <div className="w-16 h-16 flex items-center justify-center bg-warrior-black rounded-full mb-6">
                <i className="ri-trophy-line text-3xl text-white"></i>
              </div>
              <h3 className="font-display text-2xl text-warrior-black mb-4">GAME SCHEDULE</h3>
              <ul className="space-y-2 font-sans text-sm text-gray-700">
                <li className="flex items-start space-x-2"><img src="https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/80219af022d52b64a8832b24f76b499d.png" alt="Football" className="w-4 h-4 object-contain mt-0.5 flex-shrink-0" /><span><strong>Regular Season:</strong> 8-10 games</span></li>
                <li className="flex items-start space-x-2"><img src="https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/80219af022d52b64a8832b24f76b499d.png" alt="Football" className="w-4 h-4 object-contain mt-0.5 flex-shrink-0" /><span><strong>Game Day:</strong> Saturdays</span></li>
                <li className="flex items-start space-x-2"><img src="https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/80219af022d52b64a8832b24f76b499d.png" alt="Football" className="w-4 h-4 object-contain mt-0.5 flex-shrink-0" /><span><strong>Playoffs:</strong> Top teams advance</span></li>
                <li className="flex items-start space-x-2"><img src="https://static.readdy.ai/image/ad8804c8cf5327b346b1700de5ce7056/80219af022d52b64a8832b24f76b499d.png" alt="Football" className="w-4 h-4 object-contain mt-0.5 flex-shrink-0" /><span><strong>Jamborees:</strong> Pre-season scrimmages</span></li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-warrior-red">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-5xl text-white mb-6">READY TO JOIN THE WARRIORS?</h2>
          <p className="font-sans text-lg text-white/90 mb-8 max-w-2xl mx-auto">
            Registration is now open for the 2026 season. Secure your spot today and become part of the Cherokee Warriors family.
          </p>
          <a href={REGISTRATION_URL} target="_blank" rel="noopener noreferrer" className="inline-block bg-white text-warrior-red font-display text-xl px-10 py-4 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
            REGISTER NOW
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
